import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  workshopService,
  Workshop,
  WorkshopDate,
  registrationService,
  giftVoucherService,
  GiftVoucher,
} from "../services/firebase";
import Recaptcha, { RecaptchaRef } from "../components/Recaptcha";
import { recaptchaService } from "../services/recaptchaService";
import "./WorkshopDetail.css";
import RightArrow from "assets/icone/RightArrow";
import RightArrowMoblie from "assets/icone/RightArrowMoblie";
import LocationIcone from "assets/icone/LocationIcone";
import PrimaryButton from "components/PrimaryButton";
import { GIFT_VOUCHER_REDEMPTION_IN_REGISTRATION } from "../config/features";
import WorkshopCalendarIcon from "assets/icone/WorkshopCalendarIcon";
import WorkshopTimeIcon from "assets/icone/WorkshopTimeIcon";
import WorkshopPeopleIcon from "assets/icone/WorkshopPeopleIcon";
import WorkshopCalendarIconMoblie from "assets/icone/WorkshopCalendarIconMoblie";
import WorkshopTimeIconMoblie from "assets/icone/WorkshopTimeIconMoblie";
import WorkshopPeopleIconMoblie from "assets/icone/WorkshopPeopleIconMoblie";
import LocationIconeMoblie from "assets/icone/LocationIconeMoblie";
import { formatDateIsraelShort } from "../utils/dateUtils";

// Component for responsive RightArrow
const ResponsiveRightArrow: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth >= 390 && window.innerWidth <= 809);
    };

    window.scrollTo(0, 0);
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return isMobile ? <RightArrowMoblie /> : <RightArrow />;
};

const WorkshopDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Get current language
  const currentLanguage = i18n.language;

  // Get localized content based on current language
  const getLocalizedTitle = () => {
    if (!workshop) return "";
    if (currentLanguage === "ru" && workshop.titleRu) {
      return workshop.titleRu;
    }
    return workshop.title;
  };

  const getLocalizedDescription = () => {
    if (!workshop) return "";
    if (currentLanguage === "ru" && workshop.descriptionRu) {
      return workshop.descriptionRu;
    }
    return workshop.description;
  };

  const getLocalizedLocation = () => {
    if (!workshop) return "";
    if (currentLanguage === "ru" && workshop.locationRu) {
      return workshop.locationRu;
    }
    return workshop.location;
  };

  const [workshop, setWorkshop] = useState<Workshop | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState<WorkshopDate | null>(null);
  const [ticketsCount, setTicketsCount] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [recaptchaToken, setRecaptchaToken] = useState<string>("");
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const [showPhoneError, setShowPhoneError] = useState(false);
  const [voucherInput, setVoucherInput] = useState("");
  const [appliedVoucher, setAppliedVoucher] = useState<GiftVoucher | null>(null);
  const [voucherError, setVoucherError] = useState<string | null>(null);
  const [voucherChecking, setVoucherChecking] = useState(false);
  const recaptchaRef = useRef<RecaptchaRef>(null);

  useEffect(() => {
    const fetchWorkshop = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await workshopService.getById(id);
        setWorkshop(data);
      } catch (error) {
                setError("לא ניתן לטעון את הסדנה");
      } finally {
        setLoading(false);
      }
    };

    fetchWorkshop();
  }, [id]);

  const formatDate = formatDateIsraelShort;

  const formatTime = (timeString: string) => {
    return timeString;
  };

  const truncateText = (text: string, wordLimit: number = 50) => {
    const words = text.split(" ");
    if (words.length <= wordLimit) {
      return { text, isTruncated: false };
    }
    const truncatedWords = words.slice(0, wordLimit);
    return { text: truncatedWords.join(" "), isTruncated: true };
  };

  const handleDateSelect = (date: WorkshopDate) => {
    if (date.isAvailable) {
      setSelectedDate(date);
      setTicketsCount(1);
    }
  };

  const handleRegister = (date?: WorkshopDate) => {
    if (date) {
      setSelectedDate(date);
      setTicketsCount(1);
    }
    if (!selectedDate && !date) {
      alert("אנא בחר תאריך לסדנה");
      return;
    }
    setVoucherInput("");
    setAppliedVoucher(null);
    setVoucherError(null);
    setIsRegistrationModalOpen(true);
  };

  const handleRecaptchaVerify = (token: string) => {
    setRecaptchaToken(token);
    setRecaptchaVerified(true);
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken("");
    setRecaptchaVerified(false);
  };

  const handleRecaptchaError = () => {
    setRecaptchaToken("");
    setRecaptchaVerified(false);
    alert("שגיאה באימות reCAPTCHA. אנא נסה שוב.");
  };

  const voucherErrorMessage = (code?: string): string => {
    switch (code) {
      case "notFound":
        return t("voucherNotFound");
      case "notActive":
        return t("voucherNotActive");
      case "alreadyRedeemed":
        return t("voucherAlreadyRedeemed");
      default:
        return t("voucherInvalid");
    }
  };

  const handleApplyVoucher = async () => {
    const code = voucherInput.trim();
    if (!code) return;

    setVoucherChecking(true);
    setVoucherError(null);
    try {
      const result = await giftVoucherService.validateCode(code);
      if (result.valid && result.voucher) {
        setAppliedVoucher(result.voucher);
        setVoucherError(null);
      } else {
        setAppliedVoucher(null);
        setVoucherError(voucherErrorMessage(result.error));
      }
    } catch (error) {
            setAppliedVoucher(null);
      setVoucherError(t("voucherInvalid"));
    } finally {
      setVoucherChecking(false);
    }
  };

  const handleRemoveVoucher = () => {
    setAppliedVoucher(null);
    setVoucherInput("");
    setVoucherError(null);
  };

  const handleSubmitRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workshop || !id || !selectedDate) return;

    // Check if reCAPTCHA is verified
    if (!recaptchaVerified || !recaptchaToken) {
      alert("אנא אמת את reCAPTCHA לפני ההרשמה.");
      return;
    }

    // Check if consent is given
    if (!consentGiven) {
      alert("אנא סמן את תיבת ההסכמה לאיסוף המידע.");
      return;
    }

    // Check if there are enough available spots
    if (
      selectedDate.currentParticipants + ticketsCount >
      selectedDate.maxParticipants
    ) {
      alert("אין מספיק מקומות זמינים לתאריך זה.");
      return;
    }

    setSubmitting(true);
    try {
      // Verify reCAPTCHA token with Firebase function
      const verificationResult = await recaptchaService.verifyToken(
        recaptchaToken,
        "workshop_registration"
      );

      if (!verificationResult.success) {
        throw new Error(
          `reCAPTCHA verification failed: ${verificationResult.message}`
        );
      }

      // ניקוי הטופס
      setFormData({
        name: "",
        email: "",
        phone: "",
      });

      // Reset reCAPTCHA and consent
      setRecaptchaToken("");
      setRecaptchaVerified(false);
      setConsentGiven(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      // לא נסגור את המודל עד שהתשלום מוכן
      // setIsRegistrationModalOpen(false);

      // מעבר ישר לתשלום בפועל
      const paymentData = {
        workshopId: id,
        workshopTitle: getLocalizedTitle(),
        workshopDateId: selectedDate.id,
        workshopDate: selectedDate.date,
        workshopTime: selectedDate.time,
        ticketsCount: ticketsCount,
        totalAmount: workshop.price * ticketsCount,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      };

      
      // הצגת loader
      setSubmitting(true);
      setError(null);

      // יצירת הרשמה ב-Firebase (כולל שפה לתזכורת מייל)
      const registration = await registrationService.create({
        workshopId: id,
        workshopTitle: getLocalizedTitle(),
        workshopDateId: selectedDate.id,
        workshopDate: selectedDate.date,
        workshopTime: selectedDate.time,
        ticketsCount: ticketsCount,
        totalAmount: finalTotal,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        language: currentLanguage === 'ru' ? 'ru' : 'he',
        ...(appliedVoucher
          ? {
              voucherCode: appliedVoucher.code,
              voucherParticipantsCovered: coveredParticipants,
              discountAmount: voucherDiscount,
            }
          : {}),
      });

      if (!registration || !registration.id) {
        throw new Error("Registration was not created properly");
      }

      // ניקוי הטופס
      setFormData({
        name: "",
        email: "",
        phone: "",
      });

      // Reset reCAPTCHA
      setRecaptchaToken("");
      setRecaptchaVerified(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      setIsRegistrationModalOpen(false);

      if (finalTotal === 0) {
        // השובר מכסה את מלוא הסכום - אין צורך בתשלום בפועל.
        // מסמנים עסקה סינתטית כדי שזרימת האישור (PaymentSuccess) תשלח מייל
        // ותממש את השובר. ספירת המשתתפים תעודכן שם (פעם אחת בלבד).
        await registrationService.updatePaymentStatus(
          registration.id,
          "paid",
          `FREE_${Date.now()}`
        );
        try {
          await registrationService.confirmRegistrationSpots(registration.id);
        } catch (spotError) {
                  }
        navigate(`/payment/success?registrationId=${registration.id}`);
      } else {
        // הפניה לדף התשלום
        navigate(`/payment/${registration.id}`);
      }
    } catch (error) {
      
      // הודעה מפורטת יותר למשתמש
      let errorMessage = "שגיאה בהכנת התשלום. אנא נסה שוב.";
      if (error instanceof Error) {
        if (error.message.includes("reCAPTCHA")) {
          errorMessage = "שגיאה באימות האבטחה. אנא נסה שוב.";
        }
      }

      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEmailBlur = () => {
    if (formData.email) {
      setShowEmailError(!isValidEmail(formData.email));
    }
  };

  const handlePhoneBlur = () => {
    if (formData.phone) {
      setShowPhoneError(!isValidPhone(formData.phone));
    }
  };

  const handleTicketsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const count = parseInt(e.target.value);
    const maxAllowed = Math.min(
      8,
      selectedDate
        ? selectedDate.maxParticipants - selectedDate.currentParticipants
        : 1
    );
    if (count > 0 && count <= maxAllowed) {
      setTicketsCount(count);
    }
  };

  // Validation functions
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone: string) => {
    // Israeli phone number validation - accepts formats like: 050-1234567, 0501234567, +972-50-1234567
    const phoneRegex = /^(\+972|0)([23489]|5[012345689]|77)[0-9]{7}$/;
    const cleanPhone = phone.replace(/[\s-]/g, "");
    return phoneRegex.test(cleanPhone);
  };

  // Update form validation when form data changes
  useEffect(() => {
    const validateForm = () => {
      const emailValid = isValidEmail(formData.email);
      const phoneValid = isValidPhone(formData.phone);
      const nameValid = formData.name.trim().length > 0;
      const consentValid = consentGiven;
      const recaptchaValid = recaptchaVerified;

      return (
        emailValid && phoneValid && nameValid && consentValid && recaptchaValid
      );
    };

    setIsFormValid(validateForm());
  }, [formData, consentGiven, recaptchaVerified]);

  // Gift voucher discount calculation (covers up to N participants)
  const unitPrice = workshop?.price || 0;
  const baseTotal = unitPrice * ticketsCount;
  const coveredParticipants = appliedVoucher
    ? Math.min(appliedVoucher.participants, ticketsCount)
    : 0;
  const voucherDiscount = unitPrice * coveredParticipants;
  const finalTotal = Math.max(0, Number((baseTotal - voucherDiscount).toFixed(2)));

  if (loading) {
    return (
      <div className="workshop-detail-container">
        <div className="loading-container">{t("loading")}</div>
      </div>
    );
  }

  if (error || !workshop) {
    return (
      <div className="workshop-detail-container">
        <div className="error-container">{error || "הסדנה לא נמצאה"}</div>
      </div>
    );
  }

  const availableDates =
    workshop.dates?.filter((date) => date.isAvailable) || [];
  const unavailableDates =
    workshop.dates?.filter((date) => !date.isAvailable) || [];

  const hasAvailableDates =
    workshop.dates &&
    workshop.dates.length > 0 &&
    workshop.dates.some((date) => date.isAvailable);

  const hasDates = workshop.dates && workshop.dates.length > 0;

  return (
    <div className="workshop-detail-container" data-language={i18n.language} lang={i18n.language === "he" ? "he" : "ru"}>
      <Link className="back-button-workshop-detail" to="/workshops">
        <div className="back-button-workshop-detail-icon">
          <ResponsiveRightArrow />
        </div>
        {t("backToWorkshops")}
      </Link>

      {workshop.imageUrl && (
        <img
          className="workshop-detail-image"
          src={workshop.imageUrl}
          alt={workshop.title}
        />
      )}

      <div className="workshop-detail-header">
        <h1 className="workshop-detail-title">{getLocalizedTitle()}</h1>
        <div className="workshop-detail-meta">
          <div className="meta-item">
            <LocationIcone />

            {getLocalizedLocation()}
          </div>
        </div>
        <div className="availability-status-detail">
          {hasAvailableDates ? (
            <>
              <div className="availability-dot available"></div>
              <span className="availability-text available">
                {t("available")}
              </span>
            </>
          ) : (
            <>
              <div className="availability-dot unavailable"></div>
              <span className="availability-text unavailable">
                {t("unavailable")}
              </span>
            </>
          )}
        </div>
        <div className="workshop-detail-price">₪{workshop.price}</div>

        {/* Mobile meta container - only visible on mobile */}
        <div className="mobile-meta-container">
          <div className="meta-item">
            <LocationIconeMoblie />
            {getLocalizedLocation()}
          </div>
          <div className="availability-status-detail">
            {hasAvailableDates ? (
              <>
                <div className="availability-dot available"></div>
                <span className="availability-text available">
                  {t("available")}
                </span>
              </>
            ) : (
              <>
                <div className="availability-dot unavailable"></div>
                <span className="availability-text unavailable">
                  {t("unavailable")}
                </span>
              </>
            )}
          </div>
          <div className="workshop-detail-price">₪{workshop.price}</div>
        </div>
      </div>

      <div className="workshop-detail-description">
        {(() => {
          const fullText = getLocalizedDescription();

          // אם אין תאריכים זמינים, הצג את כל התיאור ללא כפתור קרא עוד
          if (!hasAvailableDates) {
            return (
              <div
                dangerouslySetInnerHTML={{
                  __html: fullText
                    .split("\n")
                    .map((paragraph) => `<p>${paragraph}</p>`)
                    .join(""),
                }}
              />
            );
          }

          // אם יש תאריכים זמינים, השתמש בלוגיקה הקיימת
          const { text, isTruncated } = truncateText(fullText, 40);

          return (
            <div>
              {isDescriptionExpanded ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: fullText
                      .split("\n")
                      .map((paragraph) => `<p>${paragraph}</p>`)
                      .join(""),
                  }}
                />
              ) : (
                <div>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: text
                        .split("\n")
                        .map((paragraph, index) => {
                          const isLastParagraph =
                            index === text.split("\n").length - 1;
                          return `<p>${paragraph}${
                            isLastParagraph && isTruncated ? "..." : ""
                          }</p>`;
                        })
                        .join(""),
                    }}
                  />
                  {isTruncated && (
                    <button
                      type="button"
                      className="read-more-link"
                      onClick={() => setIsDescriptionExpanded(true)}
                      aria-label={t("readMore")}
                    >
                      {t("readMore")}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Calendar Section - רק אם יש תאריכים זמינים */}
      {hasDates && (
        <div className="workshop-calendar-section">
          <h2 className="calendar-title">{t("chooseDate")}</h2>

          {(availableDates.length > 0 || unavailableDates.length > 0) && (
            <div className="dates-grid">
              {availableDates.map((date) => {
                const handleDateKeyDown = (e: React.KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleDateSelect(date);
                  }
                };

                return (
                <div
                  key={date.id}
                  className={`date-card available ${
                    selectedDate?.id === date.id ? "selected" : ""
                  }`}
                  onClick={() => handleDateSelect(date)}
                  onKeyDown={handleDateKeyDown}
                  role="button"
                  tabIndex={0}
                  aria-label={`${t("selectDate") || "בחר תאריך"}: ${formatDate(date.date)} ${formatTime(date.time)}`}
                >
                  <div className="date-header">
                    <WorkshopCalendarIcon className="desktop-icon" />
                    <WorkshopCalendarIconMoblie className="mobile-icon" />
                    {""}
                    <span>{formatDate(date.date)}</span>
                  </div>
                  <div className="date-time">
                    <WorkshopTimeIcon className="desktop-icon" />
                    <WorkshopTimeIconMoblie className="mobile-icon" />
                    {""}
                    <span>{formatTime(date.time)}</span>
                  </div>
                  <div className="date-participants">
                    <WorkshopPeopleIcon className="desktop-icon" />
                    <WorkshopPeopleIconMoblie className="mobile-icon" />

                    <span>
                      {date.maxParticipants - date.currentParticipants} {""}
                      {t("availableSeats")}
                    </span>
                  </div>
                  <PrimaryButton
                    fullWidth={true}
                    disabled={!date.isAvailable}
                    onClick={() => handleRegister(date)}
                  >
                    {t("register")}
                  </PrimaryButton>
                </div>
                );
              })}

              {unavailableDates.map((date) => (
                <div key={date.id} className="date-card unavailable">
                  <div className="date-header">
                    <WorkshopCalendarIcon className="desktop-icon" />
                    <WorkshopCalendarIconMoblie className="mobile-icon" />
                    <span>{formatDate(date.date)}</span>
                  </div>
                  <div className="date-time">
                    <WorkshopTimeIcon className="desktop-icon" />
                    <WorkshopTimeIconMoblie className="mobile-icon" />
                    <span>{formatTime(date.time)}</span>
                  </div>
                  <div className="date-participants">
                    <WorkshopPeopleIcon className="desktop-icon" />
                    <WorkshopPeopleIconMoblie className="mobile-icon" />
                    <span>
                      {t("full")} - {date.currentParticipants}/
                      {date.maxParticipants}
                    </span>
                  </div>
                  <PrimaryButton
                    fullWidth={true}
                    disabled={true}
                    onClick={() => handleRegister(date)}
                  >
                    {t("workshopFull")}
                  </PrimaryButton>
                </div>
              ))}
            </div>
          )}

          {workshop.dates?.length === 0 && (
            <div className="no-dates">
              <p>{t("noDatesAvailable")}</p>
            </div>
          )}
        </div>
      )}

      {isRegistrationModalOpen && (
        <div className="modal">
          <div className="modal-content-workshop-detail">
            <Link className="back-button-modal" to="/workshops">
              <span className="back-button-modal-text">
                <ResponsiveRightArrow />
                {t("backToWorkshopsDetail")}
              </span>
            </Link>
            <h1 className="modal-title-modal">{t("registrationForm")} </h1>

            <p className="modal-title-workshop"> {getLocalizedTitle()}</p>

            {submitting ? (
              <div className="payment-processing">
                <div className="spinner"></div>
                <h2>{t("processingPayment")}</h2>
                <p>{t("pleaseWaitRedirecting")}</p>
              </div>
            ) : (
              <>
                <div className="selected-date-summary">
                  <p className="selected-date-summary-item">
                    <WorkshopCalendarIcon className="desktop-icon" />
                    <WorkshopCalendarIconMoblie className="mobile-icon" />
                    {""}
                    {selectedDate && formatDate(selectedDate.date)}
                  </p>
                  <p className="selected-date-summary-item">
                    <WorkshopTimeIcon className="desktop-icon" />
                    <WorkshopTimeIconMoblie className="mobile-icon" />
                    {""}
                    {selectedDate && formatTime(selectedDate.time)}
                  </p>
                  <p className="selected-date-summary-item">
                    <WorkshopPeopleIcon className="desktop-icon" />
                    <WorkshopPeopleIconMoblie className="mobile-icon" />
                    {""}
                    {selectedDate &&
                      selectedDate.maxParticipants -
                        selectedDate.currentParticipants}
                  </p>
                </div>
                <div className="price-modal-workshop-container">
                  <p className="workshop-detail-price-modal">
                    ₪{workshop.price}{" "}
                  </p>
                </div>

                <form
                  className="form-modal"
                  onSubmit={handleSubmitRegistration}
                >
                  <div className="title-modal">
                    <h3 className="title-modal-text">
                      {t("pleaseFillOutTheFollowingDetails")}
                    </h3>
                  </div>
                  <div className="form-group-modal">
                    <input
                      className="input-modal"
                      type="text"
                      name="name"
                      placeholder={t("namePlaceholder")}
                      value={formData.name}
                      onChange={handleChange}
                      aria-label={t("namePlaceholder")}
                      required
                    />
                  </div>
                  <div className="form-group-modal">
                    <input
                      className="input-modal"
                      type="email"
                      placeholder={t("emailPlaceholder")}
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleEmailBlur}
                      aria-label={t("emailPlaceholder")}
                      required
                      aria-invalid={showEmailError}
                      aria-describedby={showEmailError ? "workshop-email-error" : undefined}
                    />
                    {showEmailError && (
                      <small 
                        className="error-text" 
                        role="alert" 
                        aria-live="polite"
                        id="workshop-email-error"
                      >
                        <span aria-hidden="true">⚠️ </span>
                        {t("invalidEmail")}
                      </small>
                    )}
                  </div>
                  <div className="form-group-modal">
                    <input
                      className="input-modal"
                      placeholder={t("phonePlaceholder")}
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => {
                        // Allow only numbers, +, and - for Israeli phone numbers
                        const value = e.target.value.replace(/[^0-9+-]/g, "");
                        setFormData((prev) => ({ ...prev, phone: value }));
                      }}
                      onBlur={handlePhoneBlur}
                      aria-label={t("phonePlaceholder")}
                      required
                    />
                    {showPhoneError && (
                      <small className="error-text">{t("invalidPhone")}</small>
                    )}
                  </div>
                  <div className="tickets-selection-modal">
                    <label className="tickets-label-modal">
                      {t("numberOfPeople")}

                      <input
                        type="number"
                        min="1"
                        max={
                          selectedDate
                            ? Math.min(
                                8,
                                selectedDate.maxParticipants -
                                  selectedDate.currentParticipants
                              )
                            : 1
                        }
                        value={ticketsCount}
                        onChange={handleTicketsChange}
                        className="tickets-input-modal"
                      />

                      {/* כפתורי + ו- למובייל */}
                      <div className="tickets-controls-mobile">
                        <button
                          type="button"
                          className="ticket-btn-mobile"
                          onClick={() => {
                            if (ticketsCount > 1) {
                              const syntheticEvent = {
                                target: {
                                  value: (ticketsCount - 1).toString(),
                                } as HTMLInputElement,
                                currentTarget: {
                                  value: (ticketsCount - 1).toString(),
                                } as HTMLInputElement,
                              } as React.ChangeEvent<HTMLInputElement>;
                              handleTicketsChange(syntheticEvent);
                            }
                          }}
                          disabled={ticketsCount <= 1}
                          aria-label={t("decreaseTickets")}
                        >
                          −
                        </button>
                        <button
                          type="button"
                          className="ticket-btn-mobile"
                          onClick={() => {
                            const maxTickets = selectedDate
                              ? Math.min(
                                  8,
                                  selectedDate.maxParticipants -
                                    selectedDate.currentParticipants
                                )
                              : 1;
                            if (ticketsCount < maxTickets) {
                              const syntheticEvent = {
                                target: {
                                  value: (ticketsCount + 1).toString(),
                                } as HTMLInputElement,
                                currentTarget: {
                                  value: (ticketsCount + 1).toString(),
                                } as HTMLInputElement,
                              } as React.ChangeEvent<HTMLInputElement>;
                              handleTicketsChange(syntheticEvent);
                            }
                          }}
                          disabled={
                            ticketsCount >=
                            (selectedDate
                              ? Math.min(
                                  8,
                                  selectedDate.maxParticipants -
                                    selectedDate.currentParticipants
                                )
                              : 1)
                          }
                          aria-label={t("increaseTickets")}
                        >
                          +
                        </button>
                      </div>
                    </label>
                  </div>
                  {GIFT_VOUCHER_REDEMPTION_IN_REGISTRATION && (
                    <>
                  <div className="voucher-section-modal">
                    <label className="voucher-label-modal" htmlFor="voucher-code-input">
                      {t("voucherCodeLabel")}
                    </label>
                    {appliedVoucher ? (
                      <div className="voucher-applied-modal">
                        <span className="voucher-applied-text">
                          ✓{" "}
                          {t("voucherApplied").replace(
                            "{{count}}",
                            String(coveredParticipants)
                          )}
                        </span>
                        <button
                          type="button"
                          className="voucher-remove-btn"
                          onClick={handleRemoveVoucher}
                        >
                          {t("voucherRemove")}
                        </button>
                      </div>
                    ) : (
                      <div className="voucher-input-row-modal">
                        <input
                          id="voucher-code-input"
                          type="text"
                          className="voucher-input-modal"
                          placeholder={t("voucherCodePlaceholder")}
                          value={voucherInput}
                          onChange={(e) => setVoucherInput(e.target.value)}
                          aria-label={t("voucherCodePlaceholder")}
                        />
                        <button
                          type="button"
                          className="voucher-apply-btn"
                          onClick={handleApplyVoucher}
                          disabled={voucherChecking || !voucherInput.trim()}
                        >
                          {voucherChecking ? t("voucherChecking") : t("voucherApply")}
                        </button>
                      </div>
                    )}
                    {voucherError && (
                      <small className="error-text" role="alert">
                        {voucherError}
                      </small>
                    )}
                  </div>

                  {appliedVoucher && voucherDiscount > 0 && (
                    <div className="voucher-discount-modal">
                      <p className="voucher-discount-modal-text">
                        {t("voucherDiscount")}: −₪{Number(voucherDiscount.toFixed(2))}
                      </p>
                    </div>
                  )}
                    </>
                  )}

                  <div className="total-price-modal">
                    <p className="total-price-modal-text">
                      {t("totalToPay")} {""}₪{finalTotal}
                    </p>
                  </div>

                  <div className="form-group-modal">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={consentGiven}
                        onChange={(e) => setConsentGiven(e.target.checked)}
                        required
                      />
                      <span className="checkbox-text-modal">
                        {t("consentCheckbox")}
                        <Link
                          to="/privacy-policy"
                          className="privacy-link-contact"
                        >
                          {t("privacyPolicy")}
                        </Link>{" "}
                        {t("consentCheckboxDescription")}
                      </span>
                    </label>
                  </div>
                  <div className="form-group-modal">
                    <label className="label-modal">{t("recaptcha")}</label>
                    <Recaptcha
                      ref={recaptchaRef}
                      siteKey={
                        process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
                        "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                      }
                      onVerify={handleRecaptchaVerify}
                      onExpired={handleRecaptchaExpired}
                      onError={handleRecaptchaError}
                      theme="light"
                      size="normal"
                      className="recaptcha-input"
                      language={i18n.language}
                    />
                  </div>

                  <div className="modal-actions-workshop">
                    <PrimaryButton
                      className="payment-transition-button-modal"
                      fullWidth={true}
                      type="submit"
                      disabled={submitting || !isFormValid}
                    >
                      {submitting ? "שולח..." : t("paymentTransition")}
                    </PrimaryButton>
                    <PrimaryButton
                      fullWidth={true}
                      type="button"
                      onClick={() => setIsRegistrationModalOpen(false)}
                      className="cancel-button-workshop"
                    >
                      {t("cancel")}
                    </PrimaryButton>
                    {/* כפתורי טסט - יימחקו בעוד שבוע */}
                    {/* <div
                      className="test-buttons-container"
                      style={{
                        marginTop: "10px",
                        borderTop: "1px solid #ddd",
                        paddingTop: "10px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          textAlign: "center",
                          marginBottom: "10px",
                        }}
                      >
                        כפתורי טסט (יימחקו בעוד שבוע)
                      </p>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <PrimaryButton
                          fullWidth={true}
                          type="button"
                          onClick={() => {
                            navigate("/payment/success");
                          }}
                          className="test-success-button"
                        >
                          טסט - תשלום הצליח
                        </PrimaryButton>
                        <PrimaryButton
                          fullWidth={true}
                          type="button"
                          onClick={() => navigate("/payment/failed")}
                          className="test-failed-button"
                        >
                          טסט - תשלום נכשל
                        </PrimaryButton>
                      </div>
                    </div> */}
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkshopDetail;
